/**
 * Iter 67by — Admin: country hero slider images.
 *
 * Curate the deck of hero banner images shown on the homepage. Each image is
 * tagged with a country; when the renter picks a country in the hero stepper,
 * the homepage swaps to that deck. The special "_default" country acts as the
 * evergreen fallback when no country deck exists.
 *
 * Upload flow: vendor/admin POSTs to /api/upload (existing endpoint) → gets
 * back a `path`; we store that `path` as `image_path`. External http(s)
 * URLs are also accepted as `image_path` for cases where the admin wants to
 * link to a CDN-hosted asset directly.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api, { API, fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ImagePlus, Loader2, Pencil, Trash2, Globe2, ImageIcon, Upload, GripVertical, Plus,
} from "lucide-react";

const DEFAULT_COUNTRY = "_default";

function _resolveSrc(path) {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : fileUrl(path);
}

function _newDraft(country = DEFAULT_COUNTRY) {
  return { country, image_path: "", caption: "", position: 0, is_active: true, time_of_day: "any" };
}

const TIME_OF_DAY_OPTIONS = [
  { value: "any",   label: "Any time" },
  { value: "day",   label: "Day (06:00–17:59)" },
  { value: "night", label: "Night (18:00–05:59)" },
];

const TIME_OF_DAY_BADGE = {
  any:   { label: "Any time", className: "bg-neutral-100 text-neutral-700" },
  day:   { label: "Day",      className: "bg-amber-100 text-amber-800" },
  night: { label: "Night",    className: "bg-indigo-100 text-indigo-800" },
};

export default function CountryHeroImagesPanel() {
  const [rows, setRows] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(_newDraft());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [imgR, cR] = await Promise.all([
        api.get("/admin/country-hero-images"),
        api.get("/locations/countries"),
      ]);
      setRows(imgR.data || []);
      setCountries(cR.data || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not load hero images.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Group rows by country for a clean two-tier display.
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const k = r.country || DEFAULT_COUNTRY;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    for (const arr of map.values()) arr.sort((a, b) => (a.position || 0) - (b.position || 0));
    // Surface _default first, then the rest alphabetically.
    return Array.from(map.entries()).sort((a, b) => {
      if (a[0] === DEFAULT_COUNTRY) return -1;
      if (b[0] === DEFAULT_COUNTRY) return 1;
      return a[0].localeCompare(b[0]);
    });
  }, [rows]);

  const onOpenCreate = (country = DEFAULT_COUNTRY) => {
    setEditing(null);
    setDraft(_newDraft(country));
    setDialogOpen(true);
  };
  const onOpenEdit = (r) => {
    setEditing(r);
    setDraft({
      country: r.country || DEFAULT_COUNTRY,
      image_path: r.image_path || "",
      caption: r.caption || "",
      position: r.position ?? 0,
      is_active: r.is_active !== false,
      time_of_day: r.time_of_day || "any",
    });
    setDialogOpen(true);
  };

  const onPickFile = () => fileRef.current?.click();
  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Please pick an image file.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft((d) => ({ ...d, image_path: data.path }));
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiError(err) || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onSave = async () => {
    if (!draft.country.trim()) { toast.error("Country is required."); return; }
    if (!draft.image_path.trim()) { toast.error("Image required."); return; }
    setSaving(true);
    try {
      const body = {
        country: draft.country.trim(),
        image_path: draft.image_path.trim(),
        caption: (draft.caption || "").trim() || null,
        position: Number(draft.position || 0),
        is_active: !!draft.is_active,
        time_of_day: draft.time_of_day || "any",
      };
      if (editing) {
        await api.patch(`/admin/country-hero-images/${editing.id}`, body);
        toast.success("Image updated.");
      } else {
        await api.post("/admin/country-hero-images", body);
        toast.success("Image added.");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (r) => {
    if (!window.confirm(`Remove this image from the ${r.country === DEFAULT_COUNTRY ? "default" : r.country} hero deck?`)) return;
    try {
      await api.delete(`/admin/country-hero-images/${r.id}`);
      toast.success("Image removed.");
      load();
    } catch (err) { toast.error(formatApiError(err) || "Delete failed."); }
  };

  const onToggleActive = async (r) => {
    try {
      await api.patch(`/admin/country-hero-images/${r.id}`, { is_active: !r.is_active });
      load();
    } catch (err) { toast.error(formatApiError(err) || "Update failed."); }
  };

  const onMove = async (r, delta) => {
    try {
      await api.patch(`/admin/country-hero-images/${r.id}`, { position: Number((r.position || 0) + delta) });
      load();
    } catch (err) { toast.error(formatApiError(err) || "Reorder failed."); }
  };

  return (
    <div className="space-y-6" data-testid="country-hero-images-panel">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-medium">Country hero slider</h3>
          <p className="text-xs text-neutral-500 max-w-2xl">
            Curate the homepage banner per country. When a renter picks a country in the hero search, the slider swaps to that deck. The <strong>Default</strong> deck plays when no country is selected (or when the chosen country has no images).
          </p>
        </div>
        <Button onClick={() => onOpenCreate(DEFAULT_COUNTRY)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="hero-image-add">
          <ImagePlus className="h-4 w-4 mr-1.5" /> Add image
        </Button>
      </div>

      {loading && (
        <div className="rounded-2xl border border-border bg-white p-10 text-center text-neutral-500">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {!loading && groups.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-white p-10 text-center">
          <ImageIcon className="h-8 w-8 text-neutral-300 mx-auto mb-2" />
          <div className="text-sm font-medium text-neutral-700">No hero images yet</div>
          <div className="text-xs text-neutral-500 mt-1 mb-4">Add at least one image to the Default deck so the homepage hero never goes blank.</div>
          <Button onClick={() => onOpenCreate(DEFAULT_COUNTRY)} className="bg-indigo-600 hover:bg-indigo-700">
            <ImagePlus className="h-4 w-4 mr-1.5" /> Add first image
          </Button>
        </div>
      )}

      {!loading && groups.map(([country, imgs]) => (
        <div key={country} className="rounded-2xl border border-border bg-white overflow-hidden" data-testid={`hero-group-${country}`}>
          <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Globe2 className="h-4 w-4 text-indigo-600" />
              <div className="font-semibold text-sm">
                {country === DEFAULT_COUNTRY ? "Default deck (fallback)" : country}
              </div>
              <Badge variant="outline" className="text-[10px]">{imgs.length} image{imgs.length === 1 ? "" : "s"}</Badge>
            </div>
            <Button variant="outline" size="sm" onClick={() => onOpenCreate(country)} data-testid={`hero-group-add-${country}`}>
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add to this country
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
            {imgs.map((r) => (
              <div
                key={r.id}
                className={`group rounded-xl border border-border overflow-hidden bg-neutral-50 transition ${r.is_active ? "" : "opacity-50"}`}
                data-testid={`hero-image-${r.id}`}
              >
                <div className="relative aspect-[16/9] bg-neutral-200 overflow-hidden">
                  <img
                    src={_resolveSrc(r.image_path)}
                    alt={r.caption || "Hero"}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 flex items-center gap-1">
                    <Badge className="bg-black/60 text-white text-[10px]">#{r.position || 0}</Badge>
                    {TIME_OF_DAY_BADGE[r.time_of_day || "any"] && (
                      <Badge className={`text-[10px] ${TIME_OF_DAY_BADGE[r.time_of_day || "any"].className}`}>
                        {TIME_OF_DAY_BADGE[r.time_of_day || "any"].label}
                      </Badge>
                    )}
                    {!r.is_active && <Badge variant="secondary" className="text-[10px]">Hidden</Badge>}
                  </div>
                </div>
                <div className="p-2.5 space-y-2">
                  <div className="text-xs text-neutral-700 line-clamp-2 min-h-[2rem]">
                    {r.caption || <span className="text-neutral-400 italic">No caption</span>}
                  </div>
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex items-center gap-0.5">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onMove(r, -1)} title="Move up (lower position)" data-testid={`hero-image-up-${r.id}`}>
                        <GripVertical className="h-3.5 w-3.5 rotate-90" />
                      </Button>
                      <Switch
                        checked={!!r.is_active}
                        onCheckedChange={() => onToggleActive(r)}
                        className="data-[state=checked]:bg-emerald-600"
                        data-testid={`hero-image-toggle-${r.id}`}
                      />
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onOpenEdit(r)} data-testid={`hero-image-edit-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => onDelete(r)} data-testid={`hero-image-delete-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={dialogOpen} onOpenChange={(o) => !saving && setDialogOpen(o)}>
        <DialogContent className="sm:max-w-lg" data-testid="hero-image-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImagePlus className="h-5 w-5 text-indigo-600" />
              {editing ? "Edit hero image" : "Add hero image"}
            </DialogTitle>
            <DialogDescription>Country-tagged. Default deck shows when no country is selected.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label>Country</Label>
              <Select
                value={draft.country}
                onValueChange={(v) => setDraft({ ...draft, country: v })}
              >
                <SelectTrigger className="mt-1" data-testid="hero-dialog-country"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value={DEFAULT_COUNTRY}>Default (fallback)</SelectItem>
                  {countries.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-neutral-500 mt-1">List comes from the existing fleet countries plus the curated baseline.</p>
            </div>

            <div>
              <Label>Image</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  value={draft.image_path}
                  onChange={(e) => setDraft({ ...draft, image_path: e.target.value })}
                  placeholder="Paste image URL or upload below"
                  className="flex-1"
                  data-testid="hero-dialog-url"
                />
                <Button variant="outline" type="button" onClick={onPickFile} disabled={uploading} data-testid="hero-dialog-upload">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1.5" /> Upload</>}
                </Button>
                <input ref={fileRef} type="file" accept="image/*" onChange={onFileChange} className="hidden" />
              </div>
              {draft.image_path && (
                <div className="mt-2 rounded-lg overflow-hidden border border-border bg-neutral-50 aspect-[16/7]" data-testid="hero-dialog-preview">
                  <img src={_resolveSrc(draft.image_path)} alt="preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Caption (optional)</Label>
                <Input
                  value={draft.caption}
                  onChange={(e) => setDraft({ ...draft, caption: e.target.value })}
                  placeholder="e.g. Discover the Riviera"
                  className="mt-1"
                  data-testid="hero-dialog-caption"
                />
              </div>
              <div>
                <Label>Position</Label>
                <Input
                  type="number" step="1" min="0"
                  value={draft.position}
                  onChange={(e) => setDraft({ ...draft, position: e.target.value })}
                  className="mt-1 tabular-nums"
                  data-testid="hero-dialog-position"
                />
              </div>
            </div>

            <div>
              <Label>Time of day</Label>
              <Select
                value={draft.time_of_day || "any"}
                onValueChange={(v) => setDraft({ ...draft, time_of_day: v })}
              >
                <SelectTrigger className="mt-1" data-testid="hero-dialog-time-of-day"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIME_OF_DAY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-neutral-500 mt-1">Day/Night follows the renter&apos;s local browser time. &quot;Any time&quot; plays around the clock.</p>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={!!draft.is_active}
                onCheckedChange={(v) => setDraft({ ...draft, is_active: !!v })}
                className="data-[state=checked]:bg-emerald-600"
                data-testid="hero-dialog-active"
              />
              <Label className="cursor-pointer" onClick={() => setDraft({ ...draft, is_active: !draft.is_active })}>
                Active in slider
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={onSave} disabled={saving || !draft.image_path} className="bg-indigo-600 hover:bg-indigo-700" data-testid="hero-dialog-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : (editing ? <Pencil className="h-4 w-4 mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />)}
              {editing ? "Save changes" : "Add image"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
